import { cx } from 'class-variance-authority'
import { format, formatDistance } from 'date-fns'
import { fr } from 'date-fns/locale/fr'
import { enGB } from 'date-fns/locale/en-GB'
import { nl } from 'date-fns/locale/nl'
import { DynamicIcon } from '@/components/atoms/DynamicIcon'
import type { DynamicIconName } from '@/components/atoms/DynamicIcon'

export type Activity = {
  id: string | number
  title?: string
  description?: string
  descriptionTarget?: string
  date?: string | number | Date
  dateFormat?: 'distance' | 'date' | 'date-time'
  icon?: DynamicIconName
  iconWeight?: 'bold' | 'duotone' | 'fill' | 'light' | 'regular' | 'thin'
  iconClassName?: string
  dotClassName?: string
  verticalLineClassName?: string
}

type ActivityBarProps = {
  activities: Activity[]
  locale: string
  className?: string
  options?: {
    hideVerticalLine?: boolean
    verticalLineClassName?: string
    hideDot?: boolean
    dotClassName?: string
  }
}

export const ActivityBar = ({
  activities,
  locale = 'fr',
  className,
  options = {
    hideVerticalLine: false,
    verticalLineClassName: '',
    hideDot: false,
    dotClassName: '',
  },
}: ActivityBarProps) => {
  const containIcon = activities?.some((activity) => activity?.icon)

  return (
    <div
      className={cx(
        'flex flex-col rounded-lg bg-transparent p-4',
        containIcon ? 'gap-6' : 'gap-4',
        className,
      )}
    >
      {activities.map((activity, index) => (
        <div key={activity?.id || index} className="relative">
          <div className="group flex items-start gap-4 rounded-lg px-2 py-1.5 transition-colors hover:bg-zinc-50/80">
            {(!options.hideVerticalLine || !options.hideDot) && (
              <div className="flex flex-col items-center">
                {!options.hideDot && (
                  <>
                    {activity?.icon ? (
                      <div
                        className={cx(
                          'flex size-8 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-zinc-200',
                          options.dotClassName || activity?.dotClassName,
                        )}
                      >
                        <DynamicIcon
                          name={activity.icon}
                          className={activity.iconClassName}
                          weight={activity.iconWeight}
                        />
                      </div>
                    ) : (
                      <div
                        className={cx(
                          'rounded-full bg-zinc-300',
                          containIcon ? 'size-5' : 'size-3',
                          options.dotClassName || activity?.dotClassName,
                        )}
                      />
                    )}
                  </>
                )}
                {!options.hideVerticalLine &&
                  index !== activities.length - 1 && (
                    <div
                      className={cx(
                        'absolute h-full w-0.5 rounded-b-full bg-zinc-200',
                        containIcon ? 'top-8' : 'top-3',
                        options.verticalLineClassName ||
                          activity?.verticalLineClassName,
                      )}
                    />
                  )}
              </div>
            )}

            <div className={cx('flex-1', containIcon ? '-mt-0.5' : '-mt-1')}>
              <div className="flex flex-col">
                {!!activity?.title && (
                  <span className="text-sm font-semibold text-zinc-900">
                    {activity.title}
                  </span>
                )}
                {!!(activity?.description || activity?.descriptionTarget) && (
                  <span className="text-sm leading-relaxed text-zinc-600">
                    {activity.description}
                    {!!activity?.descriptionTarget &&
                      activity.descriptionTarget}
                  </span>
                )}
                {!!activity?.date && (
                  <span className="mt-1 text-xs text-zinc-500">
                    {activity.dateFormat === 'distance'
                      ? formatDistance(
                          typeof activity.date === 'string'
                            ? new Date(activity.date)
                            : activity.date,
                          new Date(),
                          {
                            addSuffix: true,
                            locale:
                              locale === 'fr'
                                ? fr
                                : locale === 'en'
                                  ? enGB
                                  : nl,
                          },
                        )
                      : format(
                          typeof activity.date === 'string'
                            ? new Date(activity.date)
                            : activity.date,
                          `dd/MM/yyyy ${activity.dateFormat === 'date-time' ? 'HH:mm' : ''}`,
                        )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
